import { FC, useContext, useMemo, useState } from 'react'

import { SerializableAppointmentEntry } from '../../models/SerializableSdkTypes/SerializableAppointmentEntry'
import { AuthContext } from '../../providers'
import {
  useGetCustomerDetailQuery,
  useGetRepairOrdersQuery,
  useGetShopResourcesQuery,
  useGetVehicleDetailQuery
} from '../../redux/api'
import { loggingAssert } from '../../utils/handleError/loggingAssert'
import { localDateTimeArrayToDate } from '../../utils/localDateTimeArraytoDate/localDateTimeArrayToDate'
import { APPOINTMENT_COLORS_DARK } from '../Appointments/AppointmentColorSelect'
import { AppointmentForm } from './AppointmentForm'
import { AppointmentPreview } from './AppointmentPreview'

interface Props {
  appointment: SerializableAppointmentEntry
  submitCallBack?: () => void
  cancelCallBack?: () => void
  deleteCallBack?: () => void
}

export const AppointmentPanel: FC<Props> = ({
  appointment,
  submitCallBack,
  cancelCallBack,
  deleteCallBack
}) => {
  const { currentUser, userShopId } = useContext(AuthContext)
  const accessToken = loggingAssert('Token not set', currentUser!.accessToken)
  const shopId = loggingAssert('ShopId not set', userShopId)
  const [editing, setEditing] = useState(false)
  const { data: customers } = useGetCustomerDetailQuery(
    {
      authorization: accessToken,
      shop: shopId,
      customer: [
        loggingAssert('No customer found', appointment!.info!.customer!)
      ]
    },
    { skip: !(appointment && appointment!.info!.customer) }
  )
  const { data: vehicles } = useGetVehicleDetailQuery(
    {
      authorization: accessToken,
      shop: shopId,
      vehicle: [appointment!.info!.vehicle!]
    },
    { skip: !(appointment && appointment!.info!.vehicle) }
  )
  const { data: repairOrders } = useGetRepairOrdersQuery(
    {
      authorization: accessToken,
      shop: shopId,
      appointment: [appointment!.id!]
    },
    { skip: !appointment }
  )
  const { data: shopResources } = useGetShopResourcesQuery({
    authorization: accessToken,
    shop: shopId
  })

  const resources = useMemo(() => {
    if (appointment.info!.resources && shopResources) {
      const activeIds = shopResources.map((res) => res.id!)
      const filteredRes = appointment.info.resources.filter((id) =>
        activeIds.includes(id)
      )
      return filteredRes
    }
    return []
  }, [appointment, shopResources])

  return (
    <>
      {!editing && customers && repairOrders && (
        <AppointmentPreview
          appointment={appointment}
          vehicle={(vehicles || [])[0]}
          customer={customers[0]}
          repairOrder={repairOrders[0]}
          setEditing={setEditing}
          cancelCallBack={cancelCallBack}
          deleteCallBack={deleteCallBack}
        />
      )}
      {editing && (
        <AppointmentForm
          appointment={appointment}
          vehicle={(vehicles || [])[0]}
          customer={(customers || [])[0]}
          repairOrder={repairOrders && repairOrders[0]}
          date={localDateTimeArrayToDate(appointment.info!.startDateTime)}
          color={appointment?.info!.color || APPOINTMENT_COLORS_DARK[0]}
          resources={resources}
          cannedJobs={appointment?.info.cannedJobs || []}
          services={appointment.info.services || []}
          submitCallBack={async () => {
            if (submitCallBack) {
              await submitCallBack()
            }
            setEditing(false)
          }}
          cancelCallBack={() => setEditing(false)}
        />
      )}
    </>
  )
}
